import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// Import translation files
import deTranslation from '../locales/de/translation.json';
import enTranslation from '../locales/en/translation.json';
import esTranslation from '../locales/es/translation.json';
import frTranslation from '../locales/fr/translation.json';
import itTranslation from '../locales/it/translation.json';
import ptTranslation from '../locales/pt/translation.json';

import { setItem } from './storage';

export const defaultNS = 'translation';
export const resources = {
  en: {
    translation: enTranslation,
  },
  es: {
    translation: esTranslation,
  },
  fr: {
    translation: frTranslation,
  },
  de: {
    translation: deTranslation,
  },
  pt: {
    translation: ptTranslation,
  },
  it: {
    translation: itTranslation,
  },
} as const;

// Supported languages
export const supportedLanguages = ['en', 'es', 'fr', 'de', 'pt', 'it'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];
export const LANGUAGE_STORAGE_KEY = 'skyspecs-language';

export const getSupportedLanguage = (
  language: string | null | undefined
): SupportedLanguage | undefined => {
  if (!language) {
    return undefined;
  }

  const languageCode = language.toLowerCase().split(/[-_]/)[0];
  return supportedLanguages.find((supportedLanguage) => supportedLanguage === languageCode);
};

export const getPreferredLanguageToApply = ({
  currentLanguage,
  hasMultiLanguage,
  preferredLanguage,
}: {
  currentLanguage: string | null | undefined;
  hasMultiLanguage: boolean;
  preferredLanguage: string | null | undefined;
}): SupportedLanguage | undefined => {
  const targetLanguage = hasMultiLanguage
    ? (getSupportedLanguage(preferredLanguage) ?? 'en')
    : 'en';
  const normalizedCurrentLanguage = getSupportedLanguage(currentLanguage) ?? 'en';

  return targetLanguage !== normalizedCurrentLanguage ? targetLanguage : undefined;
};

export const cacheLanguage = (language: SupportedLanguage) => {
  setItem(LANGUAGE_STORAGE_KEY, language);
};

// Language display names
export const languageNames = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  it: 'Italiano',
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    ns: Object.keys(resources.en),
    // Resources are bundled and the detectors (localStorage/htmlTag) are
    // synchronous, so initialize synchronously. This guarantees module-scope
    // i18n.t() calls (e.g. the column/enum definitions in utils/constants that
    // run at import time) resolve real translations instead of their English
    // defaults on first load.
    initAsync: false,
    // No hardcoded `lng` — that would override LanguageDetector and pin every
    // load to English (so a switched language would never take effect). Let the
    // detector resolve the language from the bootstrap cache, falling back to
    // English. Once UserDetails loads, the server preference refreshes this
    // cache when necessary. `navigator` is intentionally omitted so users are
    // not auto-switched by their browser locale.
    fallbackLng: 'en',
    supportedLngs: supportedLanguages as unknown as string[],
    debug: process.env.NODE_ENV === 'development',

    // Language detection options
    detection: {
      order: ['localStorage', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },

    interpolation: {
      escapeValue: false, // not needed for react
    },

    react: {
      useSuspense: true,
    },
  });

export default i18n;
